/**
 * 客户管理 API 服务
 * 提供客户相关的所有API调用方法
 */
import api, { apiRequest } from '@/utils/api'

const CUSTOMER_API_BASE = '/api/customers/v2'

/**
 * 客户相关API
 */
export const customerService = {
  /**
   * 获取客户列表
   */
  async getCustomers(params = {}) {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/customers/`, params)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取客户列表失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取客户列表失败'
      }
    }
  },

  /**
   * 获取客户详情
   */
  async getCustomer(id) {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/customers/${id}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取客户详情失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取客户详情失败'
      }
    }
  },


  /**
   * 根据邮箱地址搜索客户
   */
  async searchCustomerByEmail(email) {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/customers/`, {
        email: email,
        page_size: 1
      })
      return {
        success: true,
        data: response.data.results && response.data.results.length > 0 ? response.data.results[0] : null
      }
    } catch (error) {
      console.error('根据邮箱搜索客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '搜索客户失败',
        data: null
      }
    }
  },

  /**
   * 根据邮件ID搜索客户信息（解决脱敏邮箱地址问题）
   */
  async searchCustomerByEmailId(emailId) {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/customers/search-by-email/${emailId}/`)
      return {
        success: true,
        data: response.data.data, // API返回的客户数据
        meta: response.data.meta   // API返回的元数据
      }
    } catch (error) {
      console.error('根据邮件ID搜索客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '搜索客户失败',
        data: null,
        meta: null
      }
    }
  },

  /**
   * 根据邮件ID创建客户（解决脱敏邮箱地址问题）
   */
  async createCustomerFromEmailId(emailId, customerData) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/customers/create-from-email/${emailId}/`, customerData)
      return {
        success: true,
        data: response.data.data, // API返回的完整数据（包含customer和email_info）
        meta: response.data.meta   // API返回的元数据
      }
    } catch (error) {
      console.error('根据邮件ID创建客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '创建客户失败',
        errors: error.response?.data?.errors || null,
        data: null,
        meta: null
      }
    }
  },

  /**
   * 创建客户
   */
  async createCustomer(customerData) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/customers/`, customerData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('创建客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '创建客户失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 更新客户
   */
  async updateCustomer(id, customerData) {
    try {
      const response = await apiRequest.put(`${CUSTOMER_API_BASE}/customers/${id}/`, customerData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('更新客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '更新客户失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 删除客户
   */
  async deleteCustomer(id) {
    try {
      await apiRequest.delete(`${CUSTOMER_API_BASE}/customers/${id}/`)
      return {
        success: true,
        message: '客户删除成功'
      }
    } catch (error) {
      console.error('删除客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '删除客户失败'
      }
    }
  },

  /**
   * 批量操作客户
   */
  async bulkAction(actionData) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/customers/bulk_action/`, actionData)
      return {
        success: true,
        data: response.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('批量操作失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '批量操作失败'
      }
    }
  },

  /**
   * 导出客户
   */
  async exportCustomers(exportData) {
    try {
      // 直接使用api实例来支持responseType配置
      const response = await api.post(`${CUSTOMER_API_BASE}/customers/export/`, exportData, {
        responseType: 'blob'
      })
      
      // 从响应头获取文件名
      const contentDisposition = response.headers['content-disposition']
      let filename = 'customers_export.xlsx' // 默认文件名（如果无法从响应头获取）
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      } else {
        // 如果没有Content-Disposition头，根据请求的格式设置默认文件名
        const format = exportData.format || 'excel'
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')
        filename = format === 'csv' 
          ? `customers_${timestamp}.csv`
          : `customers_${timestamp}.xlsx`
      }
      
      return {
        success: true,
        data: response.data,
        filename: filename
      }
    } catch (error) {
      console.error('导出客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '导出客户失败'
      }
    }
  },

  /**
   * 导入客户
   */
  async importCustomers(formData) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/customers/import_customers/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return {
        success: true,
        data: response.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('导入客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '导入客户失败',
        errors: error.response?.data?.errors || {}
      }
    }
  },

  /**
   * 下载导入模板
   */
  async downloadImportTemplate(format = 'excel') {
    try {
      // 根据格式选择不同的API端点
      let endpoint
      if (format === 'csv') {
        endpoint = `${CUSTOMER_API_BASE}/customers/get_csv_template/`
      } else {
        endpoint = `${CUSTOMER_API_BASE}/customers/get_excel_template/`
      }
      
      const response = await api.get(endpoint, {
        responseType: 'blob'
      })
      
      // 从响应头获取文件名
      const contentDisposition = response.headers['content-disposition']
      let filename = `customers_import_template_${format === 'csv' ? 'csv' : 'xlsx'}`
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
        }
      }
      
      return {
        success: true,
        blob: response.data,
        filename: filename
      }
    } catch (error) {
      console.error('模板下载失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '模板下载失败'
      }
    }
  },

  /**
   * 导入客户数据
   */
  async importCustomers(formData) {
    try {
      const response = await api.post(`${CUSTOMER_API_BASE}/customers/import_customers/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('客户导入失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '导入失败',
        errors: error.response?.data?.errors || []
      }
    }
  },

  /**
   * 从客户移除标签
   */
  async removeTagFromCustomer(customerId, tagId) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/customers/bulk_action/`, {
        customer_ids: [customerId],
        action: 'remove_tags',
        tag_ids: [tagId]
      })
      return {
        success: true,
        data: response.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('移除客户标签失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '移除标签失败'
      }
    }
  },

  /**
   * 删除客户
   */
  async deleteCustomer(customerId) {
    try {
      const response = await api.delete(`${CUSTOMER_API_BASE}/customers/${customerId}/`)
      
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('删除客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.detail || '删除客户失败',
        errors: error.response?.data?.errors || []
      }
    }
  }
}

/**
 * 客户标签分组相关API
 */
export const customerTagGroupService = {
  /**
   * 获取标签分组列表
   */
  async getTagGroups(params = {}) {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/tag-groups/`, params)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取标签分组列表失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取标签分组列表失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 创建标签分组
   */
  async createTagGroup(groupData) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/tag-groups/`, groupData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('创建标签分组失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '创建标签分组失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 更新标签分组
   */
  async updateTagGroup(id, groupData) {
    try {
      const response = await apiRequest.put(`${CUSTOMER_API_BASE}/tag-groups/${id}/`, groupData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('更新标签分组失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '更新标签分组失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 删除标签分组
   */
  async deleteTagGroup(id) {
    try {
      await apiRequest.delete(`${CUSTOMER_API_BASE}/tag-groups/${id}/`)
      return {
        success: true,
        message: '标签分组删除成功'
      }
    } catch (error) {
      console.error('删除标签分组失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '删除标签分组失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 对分组内标签排序
   */
  async sortTags(groupId, tagIds) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/tag-groups/${groupId}/sort_tags/`, {
        tag_ids: tagIds
      })
      return {
        success: true,
        data: response.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('标签排序失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '标签排序失败'
      }
    }
  }
}

/**
 * 客户标签相关API
 */
export const customerTagService = {
  /**
   * 获取标签列表
   */
  async getTags(params = {}) {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/tags/`, params)
      return {
        success: true,
        data: response.data
      }
  } catch (error) {
    console.error('获取标签列表失败:', error)
    return {
      success: false,
      message: error.response?.data?.message || '获取标签列表失败',
      errors: error.response?.data || {}
    }
  }
  },

  /**
   * 创建标签
   */
  async createTag(tagData) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/tags/`, tagData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('创建标签失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '创建标签失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 更新标签
   */
  async updateTag(id, tagData) {
    try {
      const response = await apiRequest.put(`${CUSTOMER_API_BASE}/tags/${id}/`, tagData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('更新标签失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '更新标签失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 删除标签
   */
  async deleteTag(id) {
    try {
      await apiRequest.delete(`${CUSTOMER_API_BASE}/tags/${id}/`)
      return {
        success: true,
        message: '标签删除成功'
      }
  } catch (error) {
    console.error('删除标签失败:', error)
    return {
      success: false,
      message: error.response?.data?.message || '删除标签失败',
      errors: error.response?.data || {}
    }
  }
  }
}

/**
 * 客户分组相关API
 */
export const customerGroupService = {
  /**
   * 获取分组列表
   */
  async getGroups(params = {}) {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/groups/`, params)
      return {
        success: true,
        data: response.data
      }
  } catch (error) {
    console.error('获取分组列表失败:', error)
    return {
      success: false,
      message: error.response?.data?.message || '获取分组列表失败',
      errors: error.response?.data || {}
    }
  }
  },

  /**
   * 创建分组
   */
  async createGroup(groupData) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/groups/`, groupData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('创建分组失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '创建分组失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 更新分组
   */
  async updateGroup(id, groupData) {
    try {
      const response = await apiRequest.put(`${CUSTOMER_API_BASE}/groups/${id}/`, groupData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('更新分组失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '更新分组失败',
        errors: error.response?.data || {}
      }
    }
  },

  /**
   * 删除分组
   */
  async deleteGroup(id) {
    try {
      await apiRequest.delete(`${CUSTOMER_API_BASE}/groups/${id}/`)
      return {
        success: true,
        message: '分组删除成功'
      }
  } catch (error) {
    console.error('删除分组失败:', error)
    return {
      success: false,
      message: error.response?.data?.message || '删除分组失败',
      errors: error.response?.data || {}
    }
  }
  },

  /**
   * 向分组添加客户
   */
  async addCustomers(groupId, customerIds) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/groups/${groupId}/add_customers/`, {
        customer_ids: customerIds
      })
      return {
        success: true,
        data: response.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('添加客户到分组失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '添加客户到分组失败'
      }
    }
  },

  /**
   * 从分组移除客户
   */
  async removeCustomers(groupId, customerIds) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/groups/${groupId}/remove_customers/`, {
        customer_ids: customerIds
      })
      return {
        success: true,
        data: response.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('从分组移除客户失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '从分组移除客户失败'
      }
    }
  }
}

/**
 * 客户自定义字段 API 服务
 */
export const customerCustomFieldService = {
  /**
   * 获取自定义字段列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} API响应
   */
  async getCustomFields(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      // 处理查询参数
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key])
        }
      })
      
      const queryString = queryParams.toString()
      const url = `${CUSTOMER_API_BASE}/custom-fields/${queryString ? `?${queryString}` : ''}`
      
      const response = await apiRequest.get(url)
      return {
        success: true,
        data: response.data,
        results: response.data.results || [],
        count: response.data.count || 0
      }
    } catch (error) {
      console.error('获取客户自定义字段列表失败:', error)
      throw error
    }
  },

  /**
   * 获取单个自定义字段详情
   * @param {number} fieldId - 字段ID
   * @returns {Promise<Object>} 字段详情
   */
  async getCustomField(fieldId) {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/custom-fields/${fieldId}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取客户自定义字段详情失败:', error)
      throw error
    }
  },

  /**
   * 创建自定义字段
   * @param {Object} fieldData - 字段数据
   * @returns {Promise<Object>} 创建结果
   */
  async createCustomField(fieldData) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/custom-fields/`, fieldData)
      return {
        success: true,
        data: response.data,
        message: '客户自定义字段创建成功'
      }
    } catch (error) {
      console.error('创建客户自定义字段失败:', error)
      throw error
    }
  },

  /**
   * 更新自定义字段
   * @param {number} fieldId - 字段ID
   * @param {Object} fieldData - 更新的字段数据
   * @returns {Promise<Object>} 更新结果
   */
  async updateCustomField(fieldId, fieldData) {
    try {
      const response = await apiRequest.put(`${CUSTOMER_API_BASE}/custom-fields/${fieldId}/`, fieldData)
      return {
        success: true,
        data: response.data,
        message: '客户自定义字段更新成功'
      }
    } catch (error) {
      console.error('更新客户自定义字段失败:', error)
      throw error
    }
  },

  /**
   * 删除自定义字段
   * @param {number} fieldId - 字段ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteCustomField(fieldId) {
    try {
      await apiRequest.delete(`${CUSTOMER_API_BASE}/custom-fields/${fieldId}/`)
      return {
        success: true,
        message: '客户自定义字段删除成功'
      }
    } catch (error) {
      console.error('删除客户自定义字段失败:', error)
      throw error
    }
  },

  /**
   * 排序自定义字段
   * @param {Array<number>} fieldIds - 字段ID列表（按新顺序排列）
   * @returns {Promise<Object>} 操作结果
   */
  async sortCustomFields(fieldIds) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/custom-fields/sort_fields/`, {
        field_ids: fieldIds
      })
      return {
        success: true,
        data: response.data,
        message: response.data.message || '字段排序更新成功'
      }
    } catch (error) {
      console.error('客户自定义字段排序失败:', error)
      throw error
    }
  },

  /**
   * 获取支持的字段类型列表
   * @returns {Promise<Object>} 字段类型列表
   */
  async getFieldTypes() {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/custom-fields/field_types/`)
      return {
        success: true,
        data: response.data.data || []
      }
    } catch (error) {
      console.error('获取字段类型列表失败:', error)
      throw error
    }
  }
}

/**
 * 客户列表字段配置 API 服务
 */
export const customerFieldConfigService = {
  /**
   * 获取字段配置列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} API响应
   */
  async getFieldConfigs(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key])
        }
      })
      
      const queryString = queryParams.toString()
      const response = await apiRequest.get(
        `${CUSTOMER_API_BASE}/field-configs/${queryString ? `?${queryString}` : ''}`
      )
      
      return {
        success: true,
        results: response.data.results || response.data,
        count: response.data.count || (response.data.results ? response.data.results.length : 0)
      }
    } catch (error) {
      console.error('获取字段配置列表失败:', error)
      throw error
    }
  },

  /**
   * 获取单个字段配置详情
   * @param {number} configId - 配置ID
   * @returns {Promise<Object>} API响应
   */
  async getFieldConfig(configId) {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/field-configs/${configId}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取字段配置详情失败:', error)
      throw error
    }
  },

  /**
   * 创建字段配置
   * @param {Object} configData - 配置数据
   * @returns {Promise<Object>} API响应
   */
  async createFieldConfig(configData) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/field-configs/`, configData)
      return {
        success: true,
        data: response.data,
        message: '字段配置创建成功'
      }
    } catch (error) {
      console.error('创建字段配置失败:', error)
      throw error
    }
  },

  /**
   * 更新字段配置
   * @param {number} configId - 配置ID
   * @param {Object} configData - 更新数据
   * @returns {Promise<Object>} API响应
   */
  async updateFieldConfig(configId, configData) {
    try {
      const response = await apiRequest.put(`${CUSTOMER_API_BASE}/field-configs/${configId}/`, configData)
      return {
        success: true,
        data: response.data,
        message: '字段配置更新成功'
      }
    } catch (error) {
      console.error('更新字段配置失败:', error)
      throw error
    }
  },

  /**
   * 删除字段配置
   * @param {number} configId - 配置ID
   * @returns {Promise<Object>} API响应
   */
  async deleteFieldConfig(configId) {
    try {
      await apiRequest.delete(`${CUSTOMER_API_BASE}/field-configs/${configId}/`)
      return {
        success: true,
        message: '字段配置删除成功'
      }
    } catch (error) {
      console.error('删除字段配置失败:', error)
      throw error
    }
  },

  /**
   * 批量更新字段配置
   * @param {Array} fieldConfigs - 字段配置列表
   * @returns {Promise<Object>} API响应
   */
  async bulkUpdateFieldConfigs(fieldConfigs) {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/field-configs/bulk_update/`, {
        field_configs: fieldConfigs
      })
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || '批量更新成功'
      }
    } catch (error) {
      console.error('批量更新字段配置失败:', error)
      throw error
    }
  },

  /**
   * 获取可见字段配置
   * @returns {Promise<Object>} API响应
   */
  async getVisibleFieldConfigs() {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/field-configs/visible_fields/`)
      return {
        success: true,
        data: response.data.data || response.data
      }
    } catch (error) {
      console.error('获取可见字段配置失败:', error)
      throw error
    }
  },

  /**
   * 初始化字段配置
   * @returns {Promise<Object>} API响应
   */
  async initializeFieldConfigs() {
    try {
      const response = await apiRequest.post(`${CUSTOMER_API_BASE}/field-configs/initialize_config/`)
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || '初始化成功'
      }
    } catch (error) {
      console.error('初始化字段配置失败:', error)
      throw error
    }
  },

  /**
   * 获取系统字段定义
   * @returns {Promise<Object>} API响应
   */
  async getSystemFields() {
    try {
      const response = await apiRequest.get(`${CUSTOMER_API_BASE}/field-configs/system_fields/`)
      return {
        success: true,
        data: response.data.data || response.data
      }
    } catch (error) {
      console.error('获取系统字段定义失败:', error)
      throw error
    }
  }
}

/**
 * 导出所有服务
 */
export default {
  customerService,
  customerTagService,
  customerTagGroupService,
  customerGroupService,
  customerCustomFieldService,
  customerFieldConfigService
}
